/**
 * Role handling shared by the route guard and the People screens.
 *
 * Stored roles (`user.system.role`) come in many spellings: `EMPLOYEE`,
 * `ADMIN`, `hr_admin`, `HR_ADMIN`, `super_admin`, `hr_manager`, Clerk's
 * `org:admin` / `org:member`, and so on. Always compare them through
 * normalizeRole().
 */

export type RoleLevel = 'employee' | 'manager' | 'admin';

/** Normalised roles that may open admin-only screens. */
export const ADMIN_ROLES: readonly string[] = [
  'admin',
  'hr_admin',
  'hr_manager',
  'super_admin',
  'owner',
  'org:admin',
];

/** Normalised roles that may open manager screens (admins may too). */
export const MANAGER_ROLES: readonly string[] = ['manager', 'line_manager'];

const LEVEL_RANK: Record<RoleLevel, number> = {
  employee: 0,
  manager: 1,
  admin: 2,
};

/** Lower-case, trimmed, with spaces and hyphens turned into underscores. */
export function normalizeRole(role: unknown): string {
  if (typeof role !== 'string') return '';
  return role
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * Anything that is not a known admin or manager role (unknown values, ids,
 * missing roles) is an employee.
 */
export function getRoleLevel(role: unknown): RoleLevel {
  const normalized = normalizeRole(role);
  if (ADMIN_ROLES.includes(normalized)) return 'admin';
  if (MANAGER_ROLES.includes(normalized)) return 'manager';
  return 'employee';
}

/** Whether `level` may open a screen that needs `required`. */
export function hasRoleLevel(level: RoleLevel, required: RoleLevel): boolean {
  return LEVEL_RANK[level] >= LEVEL_RANK[required];
}

/** Roles the People edit dialog offers, as stored in `user.system.role`. */
export const PERSON_ROLE_OPTIONS = [
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'ADMIN', label: 'Admin' },
] as const;

/**
 * The stored role behind the `role` a people list row carries.
 *
 * The list sends display labels ("Employee", "Admin") for the roles it
 * knows and "Unknown Role" for everything else (hr_admin, super_admin, ...).
 * Labels map back to their stored value, other stored values pass through
 * unchanged, and '' means the stored role is not known, so the caller must
 * leave it as it is rather than guess.
 */
export function storedRoleFromListRole(role: unknown): string {
  if (typeof role !== 'string') return '';
  const trimmed = role.trim();
  const normalized = normalizeRole(trimmed);
  if (!normalized || normalized === 'unknown_role') return '';

  const option = PERSON_ROLE_OPTIONS.find(
    ({ value, label }) =>
      normalizeRole(value) === normalized ||
      normalizeRole(label) === normalized,
  );
  return option ? option.value : trimmed;
}

/** A readable label for a stored role, e.g. `hr_admin` -> `Hr Admin`. */
export function roleLabel(role: string): string {
  const option = PERSON_ROLE_OPTIONS.find(({ value }) => value === role);
  if (option) return option.label;
  return normalizeRole(role)
    .replace(/^org:/, '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
